import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../../components/Modal';
import UserPicker from '../../components/UserPicker';
import {
    GroupInfo,
    GroupMemberRow,
    addGroupMember,
    listGroupMembers,
    removeGroupMember,
} from '../../api/authz';
import { ApiError } from '../../api/request';
import { formatTime } from '../../utils/time';
import { alertDialog, confirmDialog } from '../../utils/dialog';

/**
 * 用户组成员面板 (2026-09-13): 看 / 加 / 移出组成员. 成员须是平台用户 (未准入可预加, 准入后生效).
 */
const STATUS_ZH: Record<string, string> = {
    pending: '待审批',
    approved: '已准入',
    rejected: '已驳回',
};

function GroupMembersModal({
    group,
    onClose,
    onChanged,
}: {
    group: GroupInfo | null;
    onClose: () => void;
    onChanged?: () => void;
}) {
    const [list, setList] = useState<GroupMemberRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        if (!group) return;
        setLoading(true);
        setError('');
        try {
            const res = await listGroupMembers(group.name);
            setList(res.list || []);
        } catch (e) {
            setError(
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e),
            );
            setList([]);
        } finally {
            setLoading(false);
        }
    }, [group]);

    useEffect(() => {
        setEmail('');
        load();
    }, [load]);

    const onAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!group) return;
        const addr = email.trim();
        if (!addr) return;
        setBusy(true);
        try {
            await addGroupMember(group.name, addr);
            setEmail('');
            await load();
            onChanged?.();
        } catch (err) {
            alertDialog(
                `添加失败: ${err instanceof ApiError ? err.message : String(err)}`,
            );
        } finally {
            setBusy(false);
        }
    };

    const onRemove = async (row: GroupMemberRow) => {
        if (!group) return;
        const who = row.displayName || row.email;
        if (
            !(await confirmDialog(
                `确认将 ${who} 移出「${group.displayName}」?\n其将失去该组带来的角色 (直接绑定的角色不受影响)。`,
            ))
        )
            return;
        try {
            await removeGroupMember(group.name, row.email);
            await load();
            onChanged?.();
        } catch (err) {
            alertDialog(
                `移出失败: ${err instanceof ApiError ? err.message : String(err)}`,
            );
        }
    };

    return (
        <Modal
            open={!!group}
            title={group ? `「${group.displayName}」的成员` : ''}
            subtitle={group && <span className="font-mono">{group.name}</span>}
            onClose={onClose}
            width={680}
        >
            <div className="space-y-4">
                <form onSubmit={onAdd} className="flex items-end gap-2">
                    <label className="block flex-1">
                        <span className="mb-1 block text-xs text-slate-500">
                            从平台用户中选择加入本组
                        </span>
                        <UserPicker
                            className="w-full"
                            value={email}
                            onChange={setEmail}
                        />
                    </label>
                    <button
                        type="submit"
                        className="btn-primary whitespace-nowrap"
                        disabled={busy || !email.trim()}
                    >
                        {busy ? '处理中…' : '添加'}
                    </button>
                </form>
                {error && (
                    <div className="rounded border border-rose-200 bg-rose-50 p-2 text-sm text-rose-600">
                        {error}
                    </div>
                )}
                <div className="rounded-lg border border-slate-200 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="th">用户</th>
                                <th className="th">准入</th>
                                <th className="th">加入时间</th>
                                <th className="th w-24">操作</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td
                                        className="td text-center text-slate-400"
                                        colSpan={4}
                                    >
                                        加载中…
                                    </td>
                                </tr>
                            )}
                            {!loading && list.length === 0 && (
                                <tr>
                                    <td
                                        className="td text-center text-slate-400"
                                        colSpan={4}
                                    >
                                        暂无成员
                                    </td>
                                </tr>
                            )}
                            {!loading &&
                                list.map((row) => (
                                    <tr
                                        key={row.id}
                                        className="hover:bg-slate-50/60"
                                    >
                                        <td className="td">
                                            <div className="text-slate-800">
                                                {row.displayName || '—'}
                                            </div>
                                            <div className="font-mono text-xs text-slate-400">
                                                {row.email}
                                            </div>
                                        </td>
                                        <td className="td text-xs">
                                            {row.accessStatus ? (
                                                <span
                                                    className={`tag ${row.accessStatus === 'approved' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}
                                                >
                                                    {STATUS_ZH[
                                                        row.accessStatus
                                                    ] || row.accessStatus}
                                                </span>
                                            ) : (
                                                <span className="text-amber-600">
                                                    无用户记录
                                                </span>
                                            )}
                                        </td>
                                        <td className="td text-xs text-slate-500">
                                            {formatTime(row.createdAt)}
                                        </td>
                                        <td className="td">
                                            <button
                                                type="button"
                                                className="btn-danger-text"
                                                onClick={() => onRemove(row)}
                                            >
                                                移出
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </Modal>
    );
}

export default GroupMembersModal;
